export const AUTH_ENDPOINTS = {
  LOGIN: '/owner/login',
  // Second step when two-factor is on: {otp_token, code} -> {token}
  LOGIN_VERIFY: '/owner/login/verify',
  // Ends every owner token on every device
  LOGOUT: '/owner/logout',
  ME: '/auth/me',
};

// Migration 0037: staff users (owner only)
export const STAFF_ENDPOINTS = {
  ROLES: '/owner/staff/roles',
  LIST: '/owner/staff',
  CREATE: '/owner/staff',
  DETAILS: (id: string) => `/owner/staff/${id}`,
  UPDATE: (id: string) => `/owner/staff/${id}`,
  ACTIVATE: (id: string) => `/owner/staff/${id}/activate`,
  DEACTIVATE: (id: string) => `/owner/staff/${id}/deactivate`,
  RESET_PASSWORD: (id: string) => `/owner/staff/${id}/reset-password`,
  CHANGE_EMAIL: (id: string) => `/owner/staff/${id}/email`,
  DELETE: (id: string) => `/owner/staff/${id}`,
  LOGIN_HISTORY: (id: string) => `/owner/staff/${id}/login-history`,
};

// Migration 0037: every change on /owner/* and every login (owner only)
export const AUDIT_ENDPOINTS = {
  ACTIVITY_LOGS: '/owner/activity-logs',
  ACTIVITY_LOG: (id: string) => `/owner/activity-logs/${id}`,
  LOGIN_HISTORY: '/owner/login-history',
};

// Migration 0037: the owner's Google Authenticator two-factor login
export const TWO_FACTOR_ENDPOINTS = {
  STATUS: '/owner/security/2fa',
  SETUP: '/owner/security/2fa/setup',
  ENABLE: '/owner/security/2fa/enable',
  DISABLE: '/owner/security/2fa/disable',
  BACKUP_CODES: '/owner/security/2fa/backup-codes',
};

export const CLIENT_ENDPOINTS = {
  LIST: '/owner/clients',
  DETAILS: (id: string | number) => `/owner/clients/${id}`,
  PROFILE: (id: string | number) => `/owner/clients/${id}/profile`,
  UPDATE: (id: string | number) => `/owner/clients/${id}`,
  BLOCK: (id: string | number) => `/owner/clients/${id}/block`,
  UNBLOCK: (id: string | number) => `/owner/clients/${id}/unblock`,
  // Migration 0035: DELETE ?mode=soft|hard, and undo of a soft delete
  DELETE: (id: string | number) => `/owner/clients/${id}`,
  RESTORE: (id: string | number) => `/owner/clients/${id}/restore`,
  // The free trial's usage, as the election-app counts it (owner, super_admin, support, finance)
  TRIAL_USAGE: (id: string | number) => `/owner/clients/${id}/trial-usage`,
};

export const AGENT_ENDPOINTS = {
  LIST: '/owner/agents',
  CREATE: '/owner/agents',
  DETAILS: (id: string | number) => `/owner/agents/${id}`,
  ACTIVATE: (id: string | number) => `/owner/agents/${id}/activate`,
  DEACTIVATE: (id: string | number) => `/owner/agents/${id}/deactivate`,
  DELETE: (id: string | number) => `/owner/agents/${id}`,
  RESTORE: (id: string | number) => `/owner/agents/${id}/restore`,
};

export const PLAN_ENDPOINTS = {
  LIST: '/owner/plans',
  CREATE: '/owner/plans',
  DETAILS: (id: string | number) => `/owner/plans/${id}`,
  UPDATE: (id: string | number) => `/owner/plans/${id}`,
  DELETE: (id: string | number) => `/owner/plans/${id}`,
  ACTIVATE: (id: string | number) => `/owner/plans/${id}/activate`,
  DEACTIVATE: (id: string | number) => `/owner/plans/${id}/deactivate`,
};

export const PROMO_CODE_ENDPOINTS = {
  LIST: '/owner/promocodes',
  CREATE: '/owner/promocodes',
  DETAILS: (id: string | number) => `/owner/promocodes/${id}`,
  UPDATE: (id: string | number) => `/owner/promocodes/${id}`,
  ACTIVATE: (id: string | number) => `/owner/promocodes/${id}/activate`,
  DEACTIVATE: (id: string | number) => `/owner/promocodes/${id}/deactivate`,
  DELETE: (id: string | number) => `/owner/promocodes/${id}`,
};

// Migration 0033: the percent every partner promo code made from now on gets
export const PARTNER_CODE_SETTINGS_ENDPOINTS = {
  SETTINGS: '/owner/partner-code-settings',
};

// Migration 0036: limits of the election app's count-based free trial (append-only history)
export const TRIAL_SETTINGS_ENDPOINTS = {
  SETTINGS: '/owner/trial-settings',
};

export const PAYMENT_ENDPOINTS = {
  LIST: '/owner/payments',
  INVOICES: '/owner/invoices',
};

export const SETTING_ENDPOINTS = {
  UPDATE_GENERAL: '/settings/general',
  UPDATE_BUSINESS: '/settings/business',
  UPDATE_PAYMENT: '/settings/payment',
  UPDATE_NOTIFICATION: '/settings/notification',
  UPDATE_SECURITY: '/settings/security',
};

export const WEBSITE_FORM_ENDPOINTS = {
  QUERIES: '/owner/queries',
  PARTNER_REQUESTS: '/owner/partner-requests',
  PUBLIC_QUERIES: '/public/queries',
  PUBLIC_PARTNER_REQUESTS: '/public/partner-requests',
};

export const SUBSCRIPTION_ENDPOINTS = {
  LIST: '/owner/subscriptions',
};

export const VOTER_SIGN_ENDPOINTS = {
  LIST: '/owner/voter-signs',
  CREATE: '/owner/voter-signs',
  DETAILS: (id: string | number) => `/owner/voter-signs/${id}`,
  IMAGE: (id: string | number) => `/owner/voter-signs/${id}/image`,
  UPDATE: (id: string | number) => `/owner/voter-signs/${id}`,
  DELETE: (id: string | number) => `/owner/voter-signs/${id}`,
  LEADER_PUT: (id: string | number, slot: number | string) =>
    `/owner/voter-signs/${id}/leaders/${slot}`,
  LEADER_DELETE: (id: string | number, slot: number | string) =>
    `/owner/voter-signs/${id}/leaders/${slot}`,
  LEADER_IMAGE: (id: string | number, slot: number | string) =>
    `/owner/voter-signs/${id}/leaders/${slot}/image`,
};

export const POLLING_SCHEDULE_ENDPOINTS = {
  LIST: '/owner/polling-schedules',
  CREATE: '/owner/polling-schedules',
  DETAILS: (id: string) => `/owner/polling-schedules/${id}`,
  UPDATE: (id: string) => `/owner/polling-schedules/${id}`,
  DELETE: (id: string) => `/owner/polling-schedules/${id}`,
};

export const PARTNER_ENDPOINTS = {
  LIST: '/owner/partners',
  DETAILS: (id: string) => `/owner/partners/${id}`,
  PROFILE_ACTION: (id: string, action: 'approve' | 'reject' | 'suspend') =>
    `/owner/partners/${id}/${action}`,
  CHANNEL_ACTION: (
    id: string,
    channel: string,
    action: 'approve' | 'reject' | 'suspend'
  ) => `/owner/partners/${id}/channels/${channel}/${action}`,
  ORDERS: '/owner/partner-orders',
  SMS_NOTIFICATIONS: (id: string) => `/owner/partners/${id}/sms-notifications`,
  // Partner Phase Nine: Custom Promo Codes API (Owner Panel)
  CUSTOM_PROMO_CODES: (partnerId: string) => `/owner/partners/${partnerId}/promo-codes`,
  CUSTOM_PROMO_CODE_DETAIL: (partnerId: string, codeId: string) =>
    `/owner/partners/${partnerId}/promo-codes/${codeId}`,
  // Partner Phase Ten: Location Change Requests (Owner Panel)
  LOCATION_REQUESTS: '/owner/partner-location-requests',
  // Migration 0035: account delete
  DELETE: (id: string) => `/owner/partners/${id}`,
  RESTORE: (id: string) => `/owner/partners/${id}/restore`,
};

// Migration 0035: log of every partner / client / agent delete by the owner
export const DELETED_ACCOUNT_ENDPOINTS = {
  LIST: '/owner/deleted-accounts',
};

// Partner Phase Ten — Location Change Requests API (Owner Panel)
export const LOCATION_REQUEST_ENDPOINTS = {
  LIST: '/owner/partner-location-requests',
  DETAILS: (id: string) => `/owner/partner-location-requests/${id}`,
  APPROVE: (id: string) => `/owner/partner-location-requests/${id}/approve`,
  REJECT: (id: string) => `/owner/partner-location-requests/${id}/reject`,
};

// Partner Phase Two & Phase Six — Commission API (Owner Panel side only)
export const COMMISSION_ENDPOINTS = {
  SETTINGS: '/owner/commission-settings',
  RULES: '/owner/commission-rules',
  RULE_DEACTIVATE: (id: string) => `/owner/commission-rules/${id}/deactivate`,
  UNCONFIGURED: '/owner/commissions/unconfigured',
  RECALCULATE: '/owner/commissions/recalculate',
  LEDGER: '/owner/commissions',
  SUMMARY: '/owner/commissions/summary',
  PAYOUTS: '/owner/payouts',
  PARTNER_PAYOUTS: (partnerId: string) => `/owner/partners/${partnerId}/payouts`,
  // Phase 6: Commission Holds
  HOLD: (entryId: string) => `/owner/commissions/${entryId}/hold`,
  RELEASE: (entryId: string) => `/owner/commissions/${entryId}/release`,
  BULK_RELEASE: (partnerId: string) => `/owner/partners/${partnerId}/commission-holds/release`,
  HOLDS_HISTORY: (entryId: string) => `/owner/commissions/${entryId}/holds`,
};

// Partner Phase Six — Reports & Exports API
export const REPORT_ENDPOINTS = {
  SUMMARY: '/owner/partner-reports/summary',
  TOP_PARTNERS: '/owner/partner-reports/top-partners',
};

export const EXPORT_ENDPOINTS = {
  CSV: (kind: string) => `/owner/exports/${kind}.csv`,
};


// Partner Phase Four — KYC API (Owner Panel side only)
export const KYC_ENDPOINTS = {
  SETTINGS: '/owner/kyc-settings',
  QUEUE: '/owner/kyc/queue',
  PARTNER_KYC: (partnerId: string) => `/owner/partners/${partnerId}/kyc`,
  VERIFY: (partnerId: string, item: string) => `/owner/partners/${partnerId}/kyc/${item}/verify`,
  REJECT: (partnerId: string, item: string) => `/owner/partners/${partnerId}/kyc/${item}/reject`,
  REVEAL: (partnerId: string, item: string) => `/owner/partners/${partnerId}/kyc/reveal/${item}`,
  DOCUMENT_URL: (partnerId: string, document: string) =>
    `/owner/partners/${partnerId}/kyc/documents/${document}/url`,
};

// Creator Channel — Social Platforms API (Owner Panel side)
export const SOCIAL_PLATFORM_ENDPOINTS = {
  LIST: '/owner/social-platforms',
  CREATE: '/owner/social-platforms',
  UPDATE: (id: string) => `/owner/social-platforms/${id}`,
};

// Material Wallet — Part B: Owner Panel Endpoints (Phase 11a)
export const WALLET_ENDPOINTS = {
  SETTINGS: '/owner/wallet/settings',
  PARTNER_WALLET: (partnerId: string) => `/owner/partners/${partnerId}/wallet`,
  PARTNER_LEDGER: (partnerId: string) => `/owner/partners/${partnerId}/wallet/ledger`,
  PARTNER_CREDIT: (partnerId: string) => `/owner/partners/${partnerId}/wallet/credit`,
  PARTNER_DEBIT: (partnerId: string) => `/owner/partners/${partnerId}/wallet/debit`,
  PARTNER_RECONCILE_RECHARGE: (partnerId: string, rechargeId: string) =>
    `/owner/partners/${partnerId}/wallet/recharges/${rechargeId}/reconcile`,
  BULK_RECONCILE_RECHARGES: '/owner/wallet/recharges/reconcile',
  RECONCILIATION: '/owner/wallet/reconciliation',
  WALLETS_LIST: '/owner/wallets',
  RECHARGES_LIST: '/owner/wallet/recharges',
  SUMMARY: '/owner/wallet/summary',
};

// Material Orders & Catalog — Part B: Owner Panel Endpoints (Phase 11b & 11c)
export const MATERIAL_ENDPOINTS = {
  PRODUCTS: '/owner/material/products',
  PRODUCT_UPDATE: (code: string) => `/owner/material/products/${code}`,
  RATES: (code: string) => `/owner/material/products/${code}/rates`,
  RATE_CREATE: (code: string) => `/owner/material/products/${code}/rates`,
  SETTINGS: '/owner/material/settings',
  ORDERS: '/owner/material/orders',
  ORDER_DETAIL: (orderId: string) => `/owner/material/orders/${orderId}`,
  ORDER_CANCEL: (orderId: string) => `/owner/material/orders/${orderId}/cancel`,
  ORDER_FAIL: (orderId: string) => `/owner/material/orders/${orderId}/fail`,
  // Phase 11c: Voter Slips, Rolls, Parts, Force Actions, Cleanup, Postcards, Stuck Queue
  ORDER_FILES: (orderId: string) => `/owner/material/orders/${orderId}/files`,
  ORDER_PARTS: (orderId: string) => `/owner/material/orders/${orderId}/parts`,
  ORDER_PART_LINK: (orderId: string, partIndex: number | string) =>
    `/owner/material/orders/${orderId}/parts/${partIndex}/link`,
  ORDER_FORCE_CANCEL: (orderId: string) => `/owner/material/orders/${orderId}/force-cancel`,
  ORDER_FORCE_FAIL: (orderId: string) => `/owner/material/orders/${orderId}/force-fail`,
  ORDER_CLEANUP: (orderId: string) => `/owner/material/orders/${orderId}/cleanup`,
  POSTCARDS: '/owner/material/postcards',
  POSTCARD_DETAIL: (postcardId: string) => `/owner/material/postcards/${postcardId}`,
  JOBS_STUCK: '/owner/material/jobs/stuck',
  // Migration 0034: voter data sets (counts only, never a voter) and how long unused ones are kept
  SLIP_DATA: '/owner/material/slip-data',
  SLIP_DATA_SETTINGS: '/owner/material/slip-data-settings',
  // Phase 11d: Owner Reports & Exports
  REPORTS: {
    SUMMARY: '/owner/material/reports/summary',
    WALLET: '/owner/material/reports/wallet',
    USAGE: '/owner/material/reports/usage',
    GEOGRAPHY: '/owner/material/reports/geography',
    RISK: '/owner/material/reports/risk',
  },
  EXPORTS: {
    ORDERS_CSV: '/owner/material/exports/orders.csv',
    WALLET_LEDGER_CSV: '/owner/material/exports/wallet-ledger.csv',
    WALLET_RECHARGES_CSV: '/owner/material/exports/wallet-recharges.csv',
  },
};



// Upload PDF: the owner's library of parsed voter rolls, by place
export const ROLL_ENDPOINTS = {
  LIST: '/owner/rolls',
  TREE: '/owner/rolls/tree',
  SEARCH: '/owner/rolls/search',
  DETAILS: (id: string) => `/owner/rolls/${id}`,
  VOTERS: (id: string) => `/owner/rolls/${id}/voters`,
  PDF: (id: string) => `/owner/rolls/${id}/pdf`,
  RETRY: (id: string) => `/owner/rolls/${id}/retry`,
  VOTER: (id: string, serial: number) => `/owner/rolls/${id}/voters/${serial}`,
  VERIFY: (id: string) => `/owner/rolls/${id}/verify`,
  APPROVE: (id: string) => `/owner/rolls/${id}/approve`,
};

// Upload Requests: app Admins / Managers ask the owner to upload a ward's voter-list PDF
export const UPLOAD_REQUEST_ENDPOINTS = {
  LIST: '/owner/upload-requests',
  DETAILS: (id: string) => `/owner/upload-requests/${id}`,
  // PATCH: edit a pending request
  UPDATE: (id: string) => `/owner/upload-requests/${id}`,
  UPLOAD: (id: string) => `/owner/upload-requests/${id}/upload`,
  REJECT: (id: string) => `/owner/upload-requests/${id}/reject`,
  COMPLETE: (id: string) => `/owner/upload-requests/${id}/complete`,
  WARD_REVIEW: (id: string, ward_id: string) => `/owner/upload-requests/${id}/wards/${ward_id}/review`,
  VOTER_REVIEW: (id: string, ward_id: string, voter_id: string) =>
    `/owner/upload-requests/${id}/wards/${ward_id}/voters/${voter_id}/review`,
};

// Updates (announcements) sent to app users as a notification and kept in the app's Updates screen
export const ANNOUNCEMENT_ENDPOINTS = {
  LIST: '/owner/announcements',
  CREATE: '/owner/announcements',
  PREVIEW: '/owner/announcements/preview',
  DELETE: (id: string) => `/owner/announcements/${id}/delete`,
};

// Public location lists (no login needed)
export const LOCATION_ENDPOINTS = {
  DISTRICTS: '/partner/locations/districts',
};

// Location master: the owner adds, renames and deletes districts, panchayat samitis and villages
export const LOCATION_MASTER_ENDPOINTS = {
  STATES: '/owner/locations/states',
  DISTRICTS: '/owner/locations/districts',
  DISTRICT: (id: number) => `/owner/locations/districts/${id}`,
  SAMITIS: '/owner/locations/samitis',
  SAMITI: (id: number) => `/owner/locations/samitis/${id}`,
  VILLAGES: '/owner/locations/villages',
  VILLAGE: (id: number) => `/owner/locations/villages/${id}`,
};

// The panel's notifications: the bell, its "view all" page and browser pushes (every login)
export const NOTIFICATION_ENDPOINTS = {
  LIST: '/panel/notifications',
  UNREAD_COUNT: '/panel/notifications/unread-count',
  READ: (id: number) => `/panel/notifications/${id}/read`,
  READ_ALL: '/panel/notifications/read-all',
  PUSH: '/panel/notifications/push',
  PUSH_SUBSCRIBE: '/panel/notifications/push/subscribe',
  PUSH_UNSUBSCRIBE: '/panel/notifications/push/unsubscribe',
  PUSH_TEST: '/panel/notifications/push/test',
};
